import { useEffect, useRef, useState } from "react";

// Live microphone level (0..1) from a MediaStream, sampled ~12x/sec.
export function useMicLevel(stream, active) {
  const [level, setLevel] = useState(0);
  const levelRef = useRef(0);
  useEffect(() => {
    const track = stream?.getAudioTracks?.()[0];
    if (!active || !track) return;
    let ctx, raf, last = 0;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(new MediaStream([track]));
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      const buf = new Uint8Array(an.fftSize);
      const tick = (t) => {
        raf = requestAnimationFrame(tick);
        if (t - last < 80) return;
        last = t;
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const lv = Math.min(1, Math.sqrt(sum / buf.length) * 4);
        levelRef.current = lv;
        setLevel(lv);
      };
      ctx.resume?.();
      raf = requestAnimationFrame(tick);
    } catch (_) { /* meter is optional */ }
    return () => { cancelAnimationFrame(raf); try { ctx?.close(); } catch (_) {} };
  }, [stream, active]);
  return [level, levelRef];
}

export function MicMeter({ level, listening, aiSpeaking, className = "mt-5 pt-4 border-t border-white/5" }) {
  const bars = 24;
  const lit = Math.round(level * bars);
  const hint = aiSpeaking ? "AI is speaking" : !listening ? "Mic not listening" : level > 0.08 ? "We can hear you" : "Speak up if we can't hear you";
  return (
    <div className={className} data-testid="mic-meter">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-2">
        <span>Mic level</span>
        <span className={`normal-case tracking-normal text-xs ${listening && level > 0.08 ? "text-teal-300" : "text-slate-500"}`} data-testid="mic-meter-hint">{hint}</span>
      </div>
      <div className="flex items-end gap-1 h-8" data-testid="mic-meter-bars" data-level={level.toFixed(2)}>
        {Array.from({ length: bars }, (_, i) => (
          <span key={i} className={`flex-1 rounded-sm transition-[height,background-color] duration-100 ${i < lit ? (i > bars * 0.8 ? "bg-amber-400" : "bg-teal-400") : "bg-white/10"}`}
            style={{ height: `${30 + (i / bars) * 70}%` }} />
        ))}
      </div>
    </div>
  );
}
